"use client";

import { useState } from "react";
import { FormLayout } from "../FormLayout";
import { ImageUpload } from "../ImageUpload";
import { ContactForm } from "./sections/ContactForm";
import { ClassifiedPreview } from "./sections/ClassifiedPreview";

export function SubleaseForm({
  step,
  maxSteps,
  onNext,
  onBack,
  onClose,
  formData,
  updateFormData,
  isProcessing,
}) {
  const [title, setTitle] = useState(formData.title || "");
  const [description, setDescription] = useState(formData.description || "");
  const [images, setImages] = useState(formData.images || []);
  const [condition, setCondition] = useState(formData.condition || "");
  const [totalLeaseLength, setTotalLeaseLength] = useState(
    formData.totalLeaseLength || ""
  );
  const [remainingLease, setRemainingLease] = useState(
    formData.remainingLease || ""
  );
  const [agreementType, setAgreementType] = useState(
    formData.agreementType || ""
  );
  const [securityDeposit, setSecurityDeposit] = useState(
    formData.securityDeposit || ""
  );
  const [squareFoot, setSquareFoot] = useState(formData.squareFoot || "");
  const [bedrooms, setBedrooms] = useState(formData.bedrooms || "");
  const [bathrooms, setBathrooms] = useState(formData.bathrooms || "");
  const [price, setPrice] = useState(formData.price || "");
  const [story, setStory] = useState(formData.story || "");
  const [propertyType, setPropertyType] = useState(formData.propertyType || "");
  const [isUtilities, setIsUtilities] = useState(formData.isUtilities || null);
  const [isPreowned, setIsPreowned] = useState(formData.isPreowned || null);
  const [furnished, setFurnished] = useState(formData.furnished || "");
  const [amenities, setAmenities] = useState(formData.amenities || []);

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateStep = (stepNumber) => {
    const newErrors = {};

    switch (stepNumber) {
      case 1: // Photos
        if (images.length < 6) {
          newErrors.images = "At least 6 photos are required";
        }
        break;
      case 2: // Title
        if (!title.trim()) {
          newErrors.title = "Title is required";
        } else if (title.trim().length < 2) {
          newErrors.title = "Title must be at least 2 characters long";
        }
        break;
      case 3: // Key Details
        if (!condition) {
          newErrors.condition = "Condition rating is required";
        } else if (Number(condition) < 1 || Number(condition) > 10) {
          newErrors.condition = "Condition rating must be between 1 and 10";
        }
        if (!propertyType) {
          newErrors.propertyType = "Property type is required";
        }
        if (!totalLeaseLength) {
          newErrors.totalLeaseLength = "Total lease length is required";
        } else if (Number(totalLeaseLength) <= 0) {
          newErrors.totalLeaseLength =
            "Total lease length must be greater than 0";
        }
        if (!remainingLease) {
          newErrors.remainingLease = "Remaining lease is required";
        } else if (Number(remainingLease) <= 0) {
          newErrors.remainingLease = "Remaining lease must be greater than 0";
        } else if (Number(remainingLease) > Number(totalLeaseLength)) {
          newErrors.remainingLease =
            "Remaining lease cannot be greater than total lease length";
        }
        if (!agreementType) {
          newErrors.agreementType = "Agreement type is required";
        }
        if (!squareFoot) {
          newErrors.squareFoot = "Square footage is required";
        } else if (Number(squareFoot) <= 0) {
          newErrors.squareFoot = "Square footage must be greater than 0";
        }
        if (isUtilities === null) {
          newErrors.isUtilities = "Please specify if utilities are included";
        }
        if (isPreowned === null) {
          newErrors.isPreowned =
            "Please select if the property is preowned or new";
        }
        if (!furnished) {
          newErrors.furnished = "Furnished status is required";
        }
        if (!bedrooms) {
          newErrors.bedrooms = "Number of bedrooms is required";
        }
        if (!bathrooms) {
          newErrors.bathrooms = "Number of bathrooms is required";
        }
        break;
      case 4: // Price
        if (!price) {
          newErrors.price = "Price is required";
        } else if (price <= 0) {
          newErrors.price = "Price must be greater than 0";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Determine if Next button should be disabled
  const isNextDisabled = () => {
    switch (step) {
      case 1: // Photos
        return images.length < 6;
      case 2: // Title
        return !title.trim() || title.trim().length < 2;
      case 3: // Key Details
        // Debug each condition
        const conditionEmpty = !condition;
        const conditionInvalid =
          Number(condition) < 1 || Number(condition) > 10;
        const propertyTypeEmpty = !propertyType;
        const totalLeaseEmpty = !totalLeaseLength;
        const remainingLeaseEmpty = !remainingLease;
        const agreementTypeEmpty = !agreementType;
        const squareFootEmpty = !squareFoot;
        const squareFootInvalid = Number(squareFoot) <= 0;
        const utilitiesNull = isUtilities === null;
        const preownedNull = isPreowned === null;
        const furnishedEmpty = !furnished;
        const bedroomsEmpty = !bedrooms;
        const bathroomsEmpty = !bathrooms;
        const leaseLogicInvalid =
          Number(remainingLease) > Number(totalLeaseLength);

        // Log which conditions are failing (for debugging)
        console.log("Step 3 Validation Debug:", {
          conditionEmpty,
          conditionInvalid,
          propertyTypeEmpty,
          totalLeaseEmpty,
          remainingLeaseEmpty,
          agreementTypeEmpty,
          squareFootEmpty,
          squareFootInvalid,
          utilitiesNull,
          preownedNull,
          furnishedEmpty,
          bedroomsEmpty,
          bathroomsEmpty,
          leaseLogicInvalid,
          values: {
            condition,
            propertyType,
            totalLeaseLength,
            remainingLease,
            agreementType,
            squareFoot,
            isUtilities,
            isPreowned,
            furnished,
            bedrooms,
            bathrooms,
          },
        });

        return (
          conditionEmpty ||
          conditionInvalid ||
          propertyTypeEmpty ||
          totalLeaseEmpty ||
          remainingLeaseEmpty ||
          agreementTypeEmpty ||
          squareFootEmpty ||
          squareFootInvalid ||
          utilitiesNull ||
          preownedNull ||
          furnishedEmpty ||
          bedroomsEmpty ||
          bathroomsEmpty ||
          leaseLogicInvalid
        );
      case 4: // Price
        return !price || price <= 0;
      case 6: // Contact Form
        return (
          !formData.isContactFormValid ||
          !formData.fullName?.trim() ||
          !formData.email?.trim() ||
          !formData.phoneNumber?.trim() ||
          !formData.addressLine1?.trim() ||
          !formData.state?.trim()
        );
      default:
        return false;
    }
  };

  const handleAmenityChange = (amenity, checked) => {
    if (checked) {
      setAmenities([...amenities, amenity]);
    } else {
      setAmenities(amenities.filter((a) => a !== amenity));
    }
  };

  // Save data when moving between steps
  const handleNext = () => {
    if (!validateStep(step)) {
      return;
    }

    if (step === 3) {
      const specific_details = {
        condition: Number(condition),
        propertyType,
        totalLeaseLength: Number(totalLeaseLength),
        remainingLease: Number(remainingLease),
        agreementType,
        squareFoot: Number(squareFoot),
        isUtilities,
        isPreowned,
        furnished,
        bedrooms,
        bathrooms,
        amenities,
      };
      updateFormData({
        ...formData,
        specific_details,
      });
    } else if (step === 6) {
      updateFormData({
        ...formData,
        contactName: formData.contactName || "",
        contactEmail: formData.contactEmail || "",
        contactPhone: formData.contactPhone || "",
        preferredContact: formData.preferredContact || "",
        address: formData.address || "",
        city: formData.city || "",
        state: formData.state || "",
        zip: formData.zip || "",
        agreeTerms: formData.agreeTerms || false,
      });
    } else {
      updateFormData({
        title,
        description,
        images,
        condition,
        propertyType,
        totalLeaseLength: Number(totalLeaseLength),
        remainingLease: Number(remainingLease),
        agreementType,
        securityDeposit: securityDeposit ? Number(securityDeposit) : 0,
        squareFoot: squareFoot ? Number(squareFoot) : 0,
        isUtilities,
        isPreowned,
        furnished,
        bedrooms,
        bathrooms,
        price: Number(price),
        story,
      });
    }
    onNext();
  };

  const handleImagesChange = (newImages) => {
    setImages(newImages);
    if (newImages.length > 0 && errors.images) {
      setErrors((prev) => ({ ...prev, images: undefined }));
    }
  };

  const inputClassName =
    "w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-[#7B189F] transition-colors";
  const errorInputClassName =
    "w-full p-4 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors";

  const renderStepContent = () => {
    switch (step) {
      case 1: // Add Photos
        return (
          <div className="space-y-4">
            <ImageUpload
              category="Sublease"
              minPhotos={6}
              images={images}
              onChange={handleImagesChange}
            />
            {errors.images && (
              <p className="text-red-500 text-sm mt-1">{errors.images}</p>
            )}
          </div>
        );

      case 2: // Add Title
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 font-fraunces"
              >
                Add title of your Sublease
              </label>
              <input
                id="title"
                type="text"
                placeholder="Enter a descriptive title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: undefined }));
                  }
                }}
                className={errors.title ? errorInputClassName : inputClassName}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>
          </div>
        );

      case 3: // Key Details
        return (
          <div className="space-y-5">
            <h2 className="text-xl font-medium font-fraunces">
              Add key details about your Sublease
            </h2>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Condition Rating (1-10) *
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={condition}
                onChange={(e) => {
                  setCondition(e.target.value);
                  if (errors.condition) {
                    setErrors((prev) => ({ ...prev, condition: undefined }));
                  }
                }}
                placeholder="Enter rating from 1-10"
                className={
                  errors.condition ? errorInputClassName : inputClassName
                }
              />
              {errors.condition && (
                <p className="text-red-500 text-sm mt-1">{errors.condition}</p>
              )}
            </div>

            <div className="space-y-3">
              <label
                htmlFor="propertyType"
                className="block text-sm font-medium text-gray-700"
              >
                Property Type *
              </label>
              <select
                id="propertyType"
                value={propertyType}
                onChange={(e) => {
                  setPropertyType(e.target.value);
                  if (errors.propertyType) {
                    setErrors((prev) => ({ ...prev, propertyType: undefined }));
                  }
                }}
                className={
                  errors.propertyType ? errorInputClassName : inputClassName
                }
              >
                <option value="">Select property type</option>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="condo">Condo</option>
                <option value="townhouse">Townhouse</option>
                <option value="studio">Studio</option>
                <option value="loft">Loft</option>
                <option value="duplex">Duplex</option>
                <option value="other">Other</option>
              </select>
              {errors.propertyType && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.propertyType}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <label
                  htmlFor="totalLeaseLength"
                  className="block text-sm font-medium text-gray-700"
                >
                  Total Lease Length (months) *
                </label>
                <input
                  id="totalLeaseLength"
                  type="number"
                  min="1"
                  placeholder="e.g., 12"
                  value={totalLeaseLength}
                  onChange={(e) => {
                    setTotalLeaseLength(e.target.value);
                    if (errors.totalLeaseLength) {
                      setErrors((prev) => ({
                        ...prev,
                        totalLeaseLength: undefined,
                      }));
                    }
                  }}
                  className={
                    errors.totalLeaseLength
                      ? errorInputClassName
                      : inputClassName
                  }
                />
                {errors.totalLeaseLength && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.totalLeaseLength}
                  </p>
                )}
              </div>
              <div className="space-y-3">
                <label
                  htmlFor="remainingLease"
                  className="block text-sm font-medium text-gray-700"
                >
                  Remaining Lease (months) *
                </label>
                <input
                  id="remainingLease"
                  type="number"
                  min="1"
                  placeholder="e.g., 8"
                  value={remainingLease}
                  onChange={(e) => {
                    setRemainingLease(e.target.value);
                    if (errors.remainingLease) {
                      setErrors((prev) => ({
                        ...prev,
                        remainingLease: undefined,
                      }));
                    }
                  }}
                  className={
                    errors.remainingLease ? errorInputClassName : inputClassName
                  }
                />
                {errors.remainingLease && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.remainingLease}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <label
                htmlFor="agreementType"
                className="block text-sm font-medium text-gray-700"
              >
                Agreement Type *
              </label>
              <select
                id="agreementType"
                value={agreementType}
                onChange={(e) => {
                  setAgreementType(e.target.value);
                  if (errors.agreementType) {
                    setErrors((prev) => ({
                      ...prev,
                      agreementType: undefined,
                    }));
                  }
                }}
                className={
                  errors.agreementType ? errorInputClassName : inputClassName
                }
              >
                <option value="">Select agreement type</option>
                <option value="standard">Standard Lease</option>
                <option value="month-to-month">Month-to-Month</option>
                <option value="student">Student Housing</option>
                <option value="corporate">Corporate Housing</option>
                <option value="other">Other</option>
              </select>
              {errors.agreementType && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.agreementType}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <label
                htmlFor="squareFoot"
                className="block text-sm font-medium text-gray-700"
              >
                Square Footage (sq ft) *
              </label>
              <input
                id="squareFoot"
                type="number"
                min="1"
                placeholder="Enter square footage"
                value={squareFoot}
                onChange={(e) => {
                  setSquareFoot(e.target.value);
                  if (errors.squareFoot) {
                    setErrors((prev) => ({ ...prev, squareFoot: undefined }));
                  }
                }}
                className={
                  errors.squareFoot ? errorInputClassName : inputClassName
                }
              />
              {errors.squareFoot && (
                <p className="text-red-500 text-sm mt-1">{errors.squareFoot}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Utilities *
              </label>
              <div className="flex gap-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="utilities-included"
                    name="isUtilities"
                    value="true"
                    checked={isUtilities === true}
                    onChange={(e) => {
                      setIsUtilities(true);
                      if (errors.isUtilities) {
                        setErrors((prev) => ({
                          ...prev,
                          isUtilities: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="utilities-included"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Utilities Included
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="utilities-not-included"
                    name="isUtilities"
                    value="false"
                    checked={isUtilities === false}
                    onChange={(e) => {
                      setIsUtilities(false);
                      if (errors.isUtilities) {
                        setErrors((prev) => ({
                          ...prev,
                          isUtilities: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="utilities-not-included"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Utilities Not Included
                  </label>
                </div>
              </div>
              {errors.isUtilities && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.isUtilities}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Is the property preowned or new? *
              </label>
              <div className="flex gap-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="preowned-sublease"
                    name="isPreowned"
                    value="true"
                    checked={isPreowned === true}
                    onChange={(e) => {
                      setIsPreowned(true);
                      if (errors.isPreowned) {
                        setErrors((prev) => ({
                          ...prev,
                          isPreowned: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="preowned-sublease"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Preowned
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="new-sublease"
                    name="isPreowned"
                    value="false"
                    checked={isPreowned === false}
                    onChange={(e) => {
                      setIsPreowned(false);
                      if (errors.isPreowned) {
                        setErrors((prev) => ({
                          ...prev,
                          isPreowned: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="new-sublease"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    New
                  </label>
                </div>
              </div>
              {errors.isPreowned && (
                <p className="text-red-500 text-sm mt-1">{errors.isPreowned}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Furnished Status *
              </label>
              <div className="flex gap-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="furnished"
                    name="furnished"
                    value="furnished"
                    checked={furnished === "furnished"}
                    onChange={(e) => {
                      setFurnished(e.target.value);
                      if (errors.furnished) {
                        setErrors((prev) => ({
                          ...prev,
                          furnished: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="furnished"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Furnished
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="semi-furnished"
                    name="furnished"
                    value="semi-furnished"
                    checked={furnished === "semi-furnished"}
                    onChange={(e) => {
                      setFurnished(e.target.value);
                      if (errors.furnished) {
                        setErrors((prev) => ({
                          ...prev,
                          furnished: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="semi-furnished"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Semi Furnished
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="unfurnished"
                    name="furnished"
                    value="unfurnished"
                    checked={furnished === "unfurnished"}
                    onChange={(e) => {
                      setFurnished(e.target.value);
                      if (errors.furnished) {
                        setErrors((prev) => ({
                          ...prev,
                          furnished: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="unfurnished"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Unfurnished
                  </label>
                </div>
              </div>
              {errors.furnished && (
                <p className="text-red-500 text-sm mt-1">{errors.furnished}</p>
              )}
            </div>

            <div className="space-y-3">
              <label
                htmlFor="securityDeposit"
                className="block text-sm font-medium text-gray-700"
              >
                Security Deposit
              </label>
              <input
                id="securityDeposit"
                type="number"
                min="0"
                placeholder="Enter security deposit amount"
                value={securityDeposit}
                onChange={(e) => setSecurityDeposit(e.target.value)}
                className={inputClassName}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <label
                  htmlFor="bedrooms"
                  className="block text-sm font-medium text-gray-700"
                >
                  Bedrooms *
                </label>
                <select
                  id="bedrooms"
                  value={bedrooms}
                  onChange={(e) => {
                    setBedrooms(e.target.value);
                    if (errors.bedrooms) {
                      setErrors((prev) => ({ ...prev, bedrooms: undefined }));
                    }
                  }}
                  className={
                    errors.bedrooms ? errorInputClassName : inputClassName
                  }
                >
                  <option value="">Select bedrooms</option>
                  <option value="studio">Studio</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5+">5+</option>
                </select>
                {errors.bedrooms && (
                  <p className="text-red-500 text-sm mt-1">{errors.bedrooms}</p>
                )}
              </div>
              <div className="space-y-3">
                <label
                  htmlFor="bathrooms"
                  className="block text-sm font-medium text-gray-700"
                >
                  Bathrooms *
                </label>
                <select
                  id="bathrooms"
                  value={bathrooms}
                  onChange={(e) => {
                    setBathrooms(e.target.value);
                    if (errors.bathrooms) {
                      setErrors((prev) => ({ ...prev, bathrooms: undefined }));
                    }
                  }}
                  className={
                    errors.bathrooms ? errorInputClassName : inputClassName
                  }
                >
                  <option value="">Select bathrooms</option>
                  <option value="1">1</option>
                  <option value="1.5">1.5</option>
                  <option value="2">2</option>
                  <option value="2.5">2.5</option>
                  <option value="3+">3+</option>
                </select>
                {errors.bathrooms && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.bathrooms}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Highlight Your Amenities
              </label>
              <p className="text-sm text-gray-500 mb-4">
                that make your property stand out to attract the perfect
                tenants.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { id: "parking", label: "Parking", icon: "🚗" },
                  { id: "wifi", label: "Wi-Fi", icon: "📶" },
                  {
                    id: "air-conditioning",
                    label: "Air Conditioning",
                    icon: "❄️",
                  },
                  { id: "heating", label: "Heating", icon: "🔥" },
                  { id: "pet-friendly", label: "Pet-Friendly", icon: "🐕" },
                  {
                    id: "laundry-in-unit",
                    label: "Laundry In-Unit",
                    icon: "👕",
                  },
                  {
                    id: "washer-dryer-on-site",
                    label: "Washer/Dryer On-Site",
                    icon: "🔄",
                  },
                  { id: "balcony-patio", label: "Balcony/Patio", icon: "🏠" },
                  { id: "fireplace", label: "Fireplace", icon: "🔥" },
                  {
                    id: "fully-equipped-kitchen",
                    label: "Fully Equipped Kitchen",
                    icon: "🍳",
                  },
                  { id: "gym", label: "Gym", icon: "💪" },
                  {
                    id: "gated-community",
                    label: "Gated Community",
                    icon: "🏘️",
                  },
                  {
                    id: "security-system",
                    label: "Security System",
                    icon: "🔒",
                  },
                ].map((amenity) => (
                  <button
                    key={amenity.id}
                    type="button"
                    onClick={() =>
                      handleAmenityChange(
                        amenity.id,
                        !amenities.includes(amenity.id)
                      )
                    }
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${
                      amenities.includes(amenity.id)
                        ? "bg-black text-white border-black"
                        : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <span>{amenity.icon}</span>
                    <span className="text-sm font-medium">{amenity.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 4: // Price
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium font-fraunces">
              Select your perfect price
            </h2>
            <h4 className="text-sm font-medium font-afacad text-gray-500">
              Condition, features, and more - help others see the value!
            </h4>
            <div className="space-y-2">
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700"
              >
                Monthly Rent *
              </label>
              <input
                id="price"
                type="number"
                min="0"
                step="0.01"
                placeholder="$ Enter the monthly amount"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  if (errors.price) {
                    setErrors((prev) => ({ ...prev, price: undefined }));
                  }
                }}
                className={errors.price ? errorInputClassName : inputClassName}
              />
              {errors.price && (
                <p className="text-red-500 text-sm mt-1">{errors.price}</p>
              )}
            </div>
          </div>
        );

      case 5: // Tell story
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium font-fraunces">
              Tell the story behind your Sublease
            </h2>
            <p className="text-sm text-gray-500">
              Why sublease? Share the details!
            </p>
            <div className="space-y-2">
              <label
                htmlFor="story"
                className="block text-sm font-medium text-gray-700"
              >
                Reason for subleasing / Additional details
              </label>
              <textarea
                id="story"
                rows={6}
                placeholder="Tell your story..."
                value={story}
                onChange={(e) => setStory(e.target.value)}
                className={inputClassName}
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                More Description
              </label>
              <textarea
                id="description"
                rows={6}
                placeholder="Add a personal touch - what should tenants know?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={inputClassName}
              />
            </div>
          </div>
        );

      case 6: // Contact details
        return (
          <ContactForm formData={formData} updateFormData={updateFormData} />
        );
      case 7: // Preview
        return <ClassifiedPreview formData={formData} category="Sublease" />;
      default:
        return null;
    }
  };

  // Get step-specific title and description
  const getStepInfo = () => {
    const title = "Create Classified";
    let description = "";

    switch (step) {
      case 1:
        description = "Add photos of your Sublease";
        break;
      case 2:
        description = "Add title and description";
        break;
      case 3:
        description = "Add key details";
        break;
      case 4:
        description = "Set monthly rent";
        break;
      case 5:
        description = "Tell your story";
        break;
      case 6:
        description = "Add your contact details";
        break;
      case 7:
        description = "Preview your classified";
        break;
    }

    return { title, description };
  };

  const { title: stepTitle, description: stepDescription } = getStepInfo();

  return (
    <FormLayout
      title={stepTitle}
      description={stepDescription}
      step={step}
      maxSteps={maxSteps}
      onNext={handleNext}
      onBack={onBack}
      onClose={onClose}
      isLastStep={step === maxSteps}
      isNextDisabled={isNextDisabled()}
      isProcessing={isProcessing}
    >
      {renderStepContent()}
    </FormLayout>
  );
}
